import type { CompleteDeviceVersionObject } from "../schemas/commonSubSchemas/completeDeviceVersion.js";
import {
    NumericVersionSegment,
    TEXT_VERSION_SEGMENT_CHARACTERS,
    TextVersionSegmentString,
} from "../validators/versionSegment.js";

export const UNKNOWN_TOKEN = "?";
export const WILDCARD_TOKEN = "*";

export type SegmentKind = "numeric" | "text";

/** Complete device version segments in string and selector order. */
export const COMPLETE_DEVICE_VERSION_SEGMENTS = [
    { key: "manufacturer", kind: "text", mustBeKnown: true },
    { key: "generation", kind: "numeric", mustBeKnown: false },
    { key: "productLine", kind: "text", mustBeKnown: false },
    { key: "hardwareVersion", kind: "numeric", mustBeKnown: false },
    { key: "hardwareVariant", kind: "text", mustBeKnown: false },
    { key: "firmwareVersion", kind: "numeric", mustBeKnown: false },
] as const satisfies readonly { key: keyof CompleteDeviceVersionObject; kind: SegmentKind; mustBeKnown: boolean }[];

/** Characters of a known segment value per kind. */
const segmentValueCharacters: Record<SegmentKind, string> = {
    numeric: "0-9",
    text: TEXT_VERSION_SEGMENT_CHARACTERS,
};

const segmentValuePatterns: Record<SegmentKind, string> = {
    numeric: `[${segmentValueCharacters.numeric}]+`,
    text: `[${segmentValueCharacters.text}]+`,
};

/** Full-match regexes for a known segment value per kind. */
export const SEGMENT_VALUE_REGEXES: Record<SegmentKind, RegExp> = {
    numeric: new RegExp(`^${segmentValuePatterns.numeric}$`),
    text: new RegExp(`^${segmentValuePatterns.text}$`),
};

export const WILDCARD_ITEM_PATTERN = "\\*";
export const UNKNOWN_ITEM_PATTERN = "\\?";

const knownOrTokenItemPattern = (knownItem: string): string =>
    `(?:${knownItem}|${WILDCARD_ITEM_PATTERN}|${UNKNOWN_ITEM_PATTERN})`;

/** Selector item grammar per segment kind: a known value form, "*" or "?". */
export const ITEM_PATTERNS: Record<SegmentKind, string> = {
    numeric: knownOrTokenItemPattern(`${segmentValuePatterns.numeric}(?:[+\\-]|:${segmentValuePatterns.numeric})?`),
    text: knownOrTokenItemPattern(segmentValuePatterns.text),
};

/** Any character an item can contain. */
export const ITEM_CHARACTER_PATTERN = `[${Object.values(segmentValueCharacters).join("")}*?+:\\-]`;

/** A bare item or a "[...]" list of items, with optional spaces around list items. */
export function termPattern(item: string): string {
    return `(?:${item}|\\[ *${item}(?: *, *${item})* *\\])`;
}

/** Whether a present value is a well-formed known value of the kind. */
export const isKnownValueWellFormed: Record<SegmentKind, (value: unknown) => boolean> = {
    numeric: (value) => NumericVersionSegment.validate(value),
    text: (value) => TextVersionSegmentString.validate(value),
};

/**
 * Throws unless every segment is absent (unknown) or a well-formed known value: a non-negative safe integer, or letters
 * in any case. The manufacturer must be known. `null` is malformed.
 */
export function assertCompleteDeviceVersionIsWellFormed(completeDeviceVersion: CompleteDeviceVersionObject): void {
    COMPLETE_DEVICE_VERSION_SEGMENTS.forEach(({ key, kind, mustBeKnown }) => {
        const value: unknown = completeDeviceVersion[key];
        if (value === undefined && mustBeKnown) {
            throw new Error(`Complete device version ${key} must be known`);
        }
        if (value !== undefined && !isKnownValueWellFormed[kind](value)) {
            throw new Error(`Malformed ${key} "${value}" in complete device version`);
        }
    });
}
